import RecipeFeedbackBugWidget from "@/components/RecipeFeedbackBugWidget";
import DraggableScrollArea from "@/components/DraggableScrollArea";
import cloudflareLoader from "@/lib/cloudflare-images";
import FaqAccordionList from "@components/FaqAccordionList";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import Image from "next/legacy/image";
import Link from "next/link";
import { useState } from "react";

const AuxTabs = ({ recipe, faqs, faqIntro }) => {
  const disableChefTab =
    !recipe.chefs ||
    recipe.chefs.length === 0 ||
    (recipe.chefs[0] && recipe.chefs[0].title === "mob");
  const disableNotesTab = !recipe.notes;
  const disableUsefulInfoTab = !recipe.usefulInfo;

  const tabList = [
    {
      value: "chef",
      label: "Chef",
      disabled: disableChefTab,
    },
    { value: "tips", label: "Tips", disabled: disableNotesTab },
    { value: "faqs", label: "FAQs", disabled: !faqs || faqs.length === 0 },
    {
      value: "useful-info",
      label: "Useful Info",
      disabled: disableUsefulInfoTab,
    },
    { value: "feedback", label: "Give feedback", disabled: false },
  ];

  const [activeTab, setActiveTab] = useState(
    tabList.find((tab) => !tab.disabled)?.value
  );

  return (
    <div>
      <div className={clsx("border-b py-5")}>
        <div className={clsx("relative")}>
          <DraggableScrollArea>
            <div className={clsx("flex space-x-6 px-6 lg:px-0")}>
              {tabList
                .filter((item) => !item.disabled)
                .map((tab) => {
                  const isActive = activeTab === tab.value;

                  return (
                    <div key={tab.value} className={clsx("")}>
                      <button
                        onClick={() => setActiveTab(tab.value)}
                        className={clsx(
                          "whitespace-nowrap text-base transition ease-out",
                          {
                            "text-zinc-950": isActive,
                            "text-zinc-400 hover:text-zinc-900": !isActive,
                          }
                        )}
                      >
                        {tab.label}
                      </button>
                    </div>
                  );
                })}
            </div>
          </DraggableScrollArea>
        </div>
      </div>
      <div className={clsx("px-6 py-6 lg:px-0")}>
        <div>
          {!disableChefTab && (
            <div data-tab="chef" hidden={activeTab !== "chef"}>
              <_ChefBio chef={recipe.chefs[0]} />
            </div>
          )}
          {!disableNotesTab && (
            <div data-tab="tips" hidden={activeTab !== "tips"}>
              <div
                className={clsx("body-text-sm")}
                dangerouslySetInnerHTML={{ __html: recipe.notes }}
              />
            </div>
          )}
          {!disableUsefulInfoTab && (
            <div data-tab="useful-info" hidden={activeTab !== "useful-info"}>
              <div
                className={clsx("body-text-sm")}
                dangerouslySetInnerHTML={{ __html: recipe.usefulInfo }}
              />
            </div>
          )}

          {faqs && faqs.length > 0 && (
            <div data-tab="faqs" hidden={activeTab !== "faqs"}>
              <div className="space-y-6">
                <div
                  className={clsx("body-text-sm")}
                  dangerouslySetInnerHTML={{ __html: faqIntro }}
                />
                <FaqAccordionList faqs={faqs} />
              </div>
            </div>
          )}

          <div data-tab="feedback" hidden={activeTab !== "feedback"}>
            <RecipeFeedbackBugWidget recipe={recipe} />
          </div>
        </div>
      </div>
    </div>
  );
};

const _ChefBio = ({ chef }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-base font-medium lg:text-lg">{chef.title}</h3>
      <div className="flex gap-8">
        <div className="body-text-sm flex-1">
          <p dangerouslySetInnerHTML={{ __html: chef.summary }} />
        </div>
        {chef.image[0] && (
          <div className="w-18">
            <Link
              href={`/${chef.uri}`}
              className="relative block aspect-square"
            >
              <Image
                src={chef.image[0].url}
                alt={chef.title}
                layout="fill"
                className="rounded-full object-cover"
                loader={cloudflareLoader}
              />
            </Link>
          </div>
        )}
      </div>
      <Link
        href={`/${chef.uri}`}
        className="group flex items-center justify-between border-t py-5 transition hover:opacity-75"
      >
        <span className="font-body text-sm">View Chef profile</span>
        <div
          className={clsx(
            "ml-auto transition group-hover:-translate-y-1 group-hover:translate-x-1"
          )}
        >
          <ArrowUpRightIcon className={clsx("h-4 w-4")} />
        </div>
      </Link>
    </div>
  );
};

export default AuxTabs;
